import React, { Suspense, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { cssIdGenerator } from '../utils/identifier.utils';
import { ErrorBoundary } from '@sentry/react';
import ModuleFallback from '../components/ModuleFallback/ModuleFallback';
import ModuleSkeleton from '../components/ModuleSkeleton/ModuleSkeleton';
import { createPortal } from 'react-dom';
import { useStore } from '../store/Store';
import { DropdownScopeContext } from '../components/Dropdown/dropdown';
import { ConsentGate } from '../consent/ConsentGate';

// Lazy-load all module components — prevents the massive import waterfall
// (50+ components each with their own dependency chains).
// In dev: avoids hundreds of individual HTTP requests at startup.
// In prod: enables code splitting so only used modules are downloaded.
const Menu = React.lazy(() => import('./Menu/Menu'));
const Field = React.lazy(() => import('./Field/Field'));
const Button = React.lazy(() => import('./Button/Button').then(m => ({ default: m.Button })));
const Question = React.lazy(() => import('./Question/Question').then(m => ({ default: m.Question })));
const Event = React.lazy(() => import('./Event/Event').then(m => ({ default: m.Event })));
const SubmitButton = React.lazy(() => import('./SubmitButton/SubmitButton').then(m => ({ default: m.SubmitButton })));
const PreviousPage = React.lazy(() => import('./PreviousPage/PreviousPage').then(m => ({ default: m.PreviousPage })));
const ForwardPage = React.lazy(() => import('./ForwardPage/ForwardPage').then(m => ({ default: m.ForwardPage })));
const PaymentOptions = React.lazy(() => import('./PaymentOptions/PaymentOptions'));
const Discount = React.lazy(() => import('./Discount/Discount').then(m => ({ default: m.Discount })));
const OrderSummary = React.lazy(() => import('./OrderSummary/OrderSummary').then(m => ({ default: m.OrderSummary })));
const GroupRegistration = React.lazy(() => import('./GroupRegistration/GroupRegistration').then(m => ({ default: m.GroupRegistration })));
const FileUploader = React.lazy(() => import('./FileUploader/FileUploader').then(m => ({ default: m.FileUploader })));
const ProfileImageUploader = React.lazy(() => import('./ProfileImageUploader/ProfileImageUploader').then(m => ({ default: m.ProfileImageUploader })));
const Image = React.lazy(() => import('./Image/Image').then(m => ({ default: m.Image })));
const TextContent = React.lazy(() => import('./TextContent/TextContent').then(m => ({ default: m.TextContent })));
const IframeModule = React.lazy(() => import('./Iframe/Iframe').then(m => ({ default: m.IframeModule })));
const ContentView = React.lazy(() => import('./ContentView/ContentView').then(m => ({ default: m.ContentView })));
const Paginator = React.lazy(() => import('./Paginator/Paginator').then(m => ({ default: m.Paginator })));
const Space = React.lazy(() => import('./Space/Space').then(m => ({ default: m.Space })));
const VideoModule = React.lazy(() => import('./Video/VideoModule').then(m => ({ default: m.VideoModule })));
const EventGroup = React.lazy(() => import('./EventGroup/EventGroup').then(m => ({ default: m.EventGroup })));
const GoogleMaps = React.lazy(() => import('./GoogleMaps/GoogleMaps'));
const CalendarInvitation = React.lazy(() => import('./CalendarInvitation/CalendarInvitation'));
const QRCode = React.lazy(() => import('./QRCode/QRCode'));
const ImageGroup = React.lazy(() => import('./ImageGroup/ImageGroup').then(m => ({ default: m.ImageGroup })));
const Countdown = React.lazy(() => import('./Countdown/Countdown').then(m => ({ default: m.Countdown })));
const VoucherModule = React.lazy(() => import('./VoucherModule/VoucherModule').then(m => ({ default: m.VoucherModule })));
const ExhibitorInviteList = React.lazy(() => import('./ExhibitorInviteList/ExhibitorInviteList').then(m => ({ default: m.ExhibitorInviteList })));
const Printer = React.lazy(() => import('./Printer/Printer'));
const ContentGroup = React.lazy(() => import('./ContentGroup/ContentGroup'));
const Hotel = React.lazy(() => import('./Hotel/Hotel'));
const ConfirmMeeting = React.lazy(() => import('./ConfirmMeeting/ConfirmMeeting'));
const MatchMakingModule = React.lazy(() => import('./MatchMaking/MatchMaking'));
const Tickets = React.lazy(() => import('./Tickets/Tickets'));
const CustomField = React.lazy(() => import('./CustomField/CustomField'));
const Notification = React.lazy(() => import('./Notification/Notification'));
const AttendeeList = React.lazy(() => import('./AttendeeList/AttendeeList'));
const ExhibitorList = React.lazy(() => import('./ExhibitorList/ExhibitorList'));
const ExhibitorEdit = React.lazy(() => import('./ExhibitorEdit/ExhibitorEdit'));
const AddToHomeScreen = React.lazy(() => import('./add-to-home-screen/add-to-home-screen').then(m => ({ default: m.AddToHomeScreen })));
const Unregister = React.lazy(() => import('./Unregister/Unregister'));
const Accreditation = React.lazy(() => import('./Accreditation/Accreditation'));
const AbstractManagement = React.lazy(() => import('./AbstractManagement/AbstractManagement'));
const AbstractAuthors = React.lazy(() => import('./AbstractAuthors/AbstractAuthors'));
const AbstractKeywords = React.lazy(() => import('./AbstractKeywords/AbstractKeywords'));
const AbstractSubmit = React.lazy(() => import('./AbstractSubmit/AbstractSubmit'));
const AbstractText = React.lazy(() => import('./AbstractText/AbstractText'));
const AbstractTitle = React.lazy(() => import('./AbstractTitle/AbstractTitle'));
const AbstractTopics = React.lazy(() => import('./AbstractTopics/AbstractTopics'));
const PresentationType = React.lazy(() => import('./PresentationType/PresentationType'));
const AbstractReview = React.lazy(() => import('./AbstractReview/AbstractReview').then(m => ({ default: m.AbstractReview })));
const FileCatalog = React.lazy(() => import('./file-catalog/file-catalog').then(m => ({ default: m.FileCatalog })));
const Sessions = React.lazy(() => import('./Session/Sessions').then(m => ({ default: m.Sessions })));
const SessionList = React.lazy(() => import('./Session/SessionList').then(m => ({ default: m.SessionList })));
const SessionDetails = React.lazy(() => import('./Session/SessionDetails').then(m => ({ default: m.SessionDetails })));
const SpeakerList = React.lazy(() => import('./Speaker/SpeakerList').then(m => ({ default: m.SpeakerList })));
const SpeakerDetails = React.lazy(() => import('./Speaker/SpeakerDetails').then(m => ({ default: m.SpeakerDetails })));
const AbstractList = React.lazy(() => import('./Abstract/AbstractList').then(m => ({ default: m.AbstractList })));
const AbstractDetails = React.lazy(() => import('./Abstract/AbstractDetails').then(m => ({ default: m.AbstractDetails })));
const Favorites = React.lazy(() => import('./Favorites/Favorites').then(m => ({ default: m.Favorites })));
const ExhibitorRegistration = React.lazy(() => import('./ExhibitorRegistration/ExhibitorRegistration').then(m => ({ default: m.ExhibitorRegistration })));
const Faq = React.lazy(() => import('./Faq/Faq').then(m => ({ default: m.Faq })));
const ExhibitorDetails = React.lazy(() => import('./ExhibitorDetails/ExhibitorDetails').then(m => ({ default: m.ExhibitorDetails })));

const NON_AUTO_HEIGHT_MODULES = ['SPACE', 'GOOGLE_MAPS', 'CONTENT_VIEW', 'IFRAME'];

export const ContentLayoutModule = observer(props => {
  const store = useStore();
  const [aspectRatio, setAspectRatio] = useState(null);
  const [renderSticky, setRenderSticky] = useState(false);
  const moduleRef = useRef();

  const { contentLayoutModuleId, type, settings, height, modulesRefs, conditions, hiddenElements, isSmartphone, activePersonNo } = props;

  const cssId = props.cssId || cssIdGenerator(contentLayoutModuleId);
  const settingsOverride = { ...settings };

  useEffect(() => {
    props.checkConditions(contentLayoutModuleId);
  }, [activePersonNo]);

  useEffect(() => {
    if (settings?.aspectRatio && !aspectRatio) {
      setAspectRatio(settings.aspectRatio);
    } else if (!settings?.aspectRatio && aspectRatio) {
      setAspectRatio(null);
    }
  }, [settings?.aspectRatio]);

  if (!modulesRefs) return null;
  if ((conditions[contentLayoutModuleId] && conditions[contentLayoutModuleId].hidden == true) || (isSmartphone && settingsOverride.hideOnMobile))
    settingsOverride.hidden = true;
  if (conditions[contentLayoutModuleId] && conditions[contentLayoutModuleId].required == true) settingsOverride.required = true;
  if (conditions[contentLayoutModuleId] && conditions[contentLayoutModuleId].readonly == true) settingsOverride.readonly = true;
  if (type === 'QUESTION' && store.abstractTaggedQuestionIds?.includes(settings.questionId) && !store.isCurrentAbstractEditable) settingsOverride.readonly = true;
  if (renderSticky) {
    return createPortal(
      <div className="fixed left-0 top-0 w-screen flex justify-center items-center content-wrapper" style={{ maxWidth: '100%', padding: 0 }}>
        <div
          id={cssId}
          ref={moduleRef}
          className={`content-wrapper ${settings.prefixClassName}`}
          style={{
            display: settingsOverride.hidden ? 'none' : null,
            maxHeight: aspectRatio ? 'unset' : NON_AUTO_HEIGHT_MODULES.includes(type) ? `calc(${height}px )` : undefined,
            aspectRatio: aspectRatio ? `${aspectRatio} / 1` : undefined,
          }}
        >
          <DropdownScopeContext.Provider value={cssId}>
            <Suspense fallback={SKELETON_MAP[type] ? <ModuleSkeleton variant={SKELETON_MAP[type]} /> : null}>
              <Module {...props} cssId={cssId} settings={settingsOverride} setAspectRatio={setAspectRatio} setRenderSticky={setRenderSticky} renderSticky={renderSticky} resourcesLoaded={store.resourcesLoaded} resourcesFailed={store.resourcesFailed} />
            </Suspense>
          </DropdownScopeContext.Provider>
        </div>
      </div>,
      document.getElementById('body')
    );
  }

  return (
    <div
      id={`${cssId}-grid-item`}
      className={'wrapper-grid-item'}
      style={{ width: '100%', display: 'flex', height: '100%', justifyContent: 'center', padding: 0, alignItems: 'flex-start', minHeight: 0 }}
    >
      <div
        id={cssId}
        ref={moduleRef}
        className={`content-wrapper ${settings.imitateField ? 'cms-field' : settings.prefixClassName}`}
        style={{
          display: settingsOverride.hidden ? 'none' : null,
          marginTop: hiddenElements ? hiddenElements * -1 + 'px' : null,
          height: aspectRatio ? 'unset' : NON_AUTO_HEIGHT_MODULES.includes(type) ? `calc(${height}px )` : undefined,
          aspectRatio: aspectRatio ? `${aspectRatio}` : undefined,
          padding: type === 'UNREGISTER' && !settings?.allowAll ? '0px' : null,
          width: '100%',
          minHeight: 0,
        }}
      >
        <ErrorBoundary fallback={err => <ModuleFallback {...{ ...err, ...props }} />}>
          <DropdownScopeContext.Provider value={cssId}>
            <Suspense fallback={SKELETON_MAP[type] ? <ModuleSkeleton variant={SKELETON_MAP[type]} /> : null}>
              <Module
                {...props}
                cssId={cssId}
                settings={settingsOverride}
                setAspectRatio={setAspectRatio}
                setRenderSticky={setRenderSticky}
                renderSticky={renderSticky}
                checkHeights={() => null}
                moduleRef={moduleRef}
                resourcesLoaded={store.resourcesLoaded}
                resourcesFailed={store.resourcesFailed}
              />
            </Suspense>
          </DropdownScopeContext.Provider>
        </ErrorBoundary>
      </div>
    </div>
  );
});

// Maps module types to skeleton variants for loading placeholders
const SKELETON_MAP = {
  FIELD: 'field',
  CUSTOMFIELD: 'field',
  QUESTION: 'question',
  TEXT_CONTENT: 'text-content',
  EVENT: 'event',
  EVENT_GROUP: 'event-group',
  HOTEL_MODULE: 'hotel',
  IMAGE: 'image',
  IMAGE_GROUP: 'image',
  VIDEO: 'video',
  CALENDAR_INVITATION: 'button',
  SUBMIT_BUTTON: 'button',
  FORWARD_PAGE: 'button',
  PREVIOUS_PAGE: 'button',
  BUTTON: 'button',
  ORDER_SUMMARY: 'order-summary',
  PAYMENT_OPTIONS: 'payment',
  PAYMENT_OPTIONS_TWO: 'payment',
  GROUP_REGISTRATION: 'group-registration',
  FILE_UPLOADER: 'file-uploader',
  DISCOUNT: 'discount',
  VOUCHER: 'order-summary',
  ACCREDITATION: 'order-summary',
  EXHIBITOR_INVITE_LIST: 'order-summary',
  EXHIBITOR_EDIT: 'order-summary',
  EXHIBITOR_REGISTRATION: 'field',
  TICKETS: 'order-summary',
  UNREGISTER: 'order-summary',
  MATCHMAKING: 'order-summary',
};

// Module types that depend on lazy-loaded resource data (Phase 2)
const RESOURCE_DEPENDENT = new Set(['FIELD', 'QUESTION', 'EVENT', 'EVENT_GROUP', 'CALENDAR_INVITATION', 'HOTEL_MODULE', 'VOUCHER', 'ORDER_SUMMARY', 'PAYMENT_OPTIONS', 'PAYMENT_OPTIONS_TWO', 'ACCREDITATION', 'EXHIBITOR_INVITE_LIST', 'EXHIBITOR_EDIT', 'EXHIBITOR_REGISTRATION', 'TICKETS', 'UNREGISTER', 'MATCHMAKING']);

const Module = ({ type, ...rest }) => {
  // Show skeleton for modules that depend on lazy-loaded resources
  if (!rest.resourcesLoaded && RESOURCE_DEPENDENT.has(type)) {
    return <ModuleSkeleton variant={SKELETON_MAP[type] || 'input'} />;
  }
  if (rest.resourcesFailed && RESOURCE_DEPENDENT.has(type)) {
    return <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>Failed to load. Please refresh the page.</div>;
  }

  switch (type) {
    case 'UNREGISTER': {
      return <Unregister {...rest} />;
    }
    case 'ACCREDITATION': {
      return <Accreditation {...rest} />;
    }
    case 'FIELD':
      return <Field {...rest} />;
    case 'IMAGE':
      return <Image {...rest} />;
    case 'COUNTDOWN':
      return <Countdown {...rest} />;
    case 'IMAGE_GROUP':
      return <ImageGroup {...rest} />;
    case 'QUESTION':
      return <Question {...rest} />;
    case 'SPACE':
      return <Space {...rest} />;
    case 'IFRAME':
      return (
        <ConsentGate kind="content">
          <IframeModule {...rest} />
        </ConsentGate>
      );
    case 'GOOGLE_MAPS':
      return (
        <ConsentGate kind="map">
          <GoogleMaps {...rest} />
        </ConsentGate>
      );
    case 'CALENDAR_INVITATION':
      return <CalendarInvitation {...rest} />;
    case 'QR_CODE':
      return <QRCode {...rest} />;
    case 'CONFIRM_MEETING':
      return <ConfirmMeeting {...rest} />;
    case 'EVENT':
      return <Event {...rest} />;
    case 'EVENT_GROUP':
      return <EventGroup {...rest} />;
    case 'MENU':
      return <Menu {...rest} />;
    case 'SUBMIT_BUTTON':
      return <SubmitButton {...rest} />;
    case 'ADD_TO_HOME_SCREEN':
      return <AddToHomeScreen {...rest} />;
    case 'CUSTOMFIELD': {
      return <CustomField {...rest} />;
    }
    case 'FORWARD_PAGE':
      return <ForwardPage {...rest} />;
    case 'PREVIOUS_PAGE':
      return <PreviousPage {...rest} />;
    case 'BUTTON':
      return <Button {...rest} />;
    case 'PRINTER': {
      return <Printer {...rest} />;
    }
    case 'CONTENT_GROUP': {
      return <ContentGroup {...rest} />;
    }
    case 'PAYMENT_OPTIONS':
    case 'PAYMENT_OPTIONS_TWO':
      return <PaymentOptions {...rest} />;
    case 'DISCOUNT':
      return <Discount {...rest} />;
    case 'ORDER_SUMMARY':
      return <OrderSummary {...rest} />;
    case 'GROUP_REGISTRATION':
      return <GroupRegistration {...rest} />;
    case 'PROFILEIMAGE_UPLOADER':
      return <ProfileImageUploader {...rest} />;
    case 'FILE_UPLOADER':
      return <FileUploader {...rest} />;
    case 'TEXT_CONTENT':
      return <TextContent {...rest} />;
    case 'CONTENT_VIEW':
      return <ContentView {...rest} />;
    case 'PAGINATOR':
      return <Paginator {...rest} />;
    case 'VIDEO':
      return <VideoModule {...{ ...rest, type }} />;
    case 'HOTEL_MODULE': {
      return <Hotel {...rest} />;
    }
    case 'MATCHMAKING':
      return <MatchMakingModule {...rest} />;
    case 'VOUCHER':
      return <VoucherModule {...rest} />;
    case 'EXHIBITOR_INVITE_LIST':
      return <ExhibitorInviteList {...rest} />;
    case 'NOTIFICATION':
      return <Notification {...rest} />;
    case 'TICKETS':
      return <Tickets {...rest} />;
    case 'ATTENDEE_LIST':
      return <AttendeeList {...rest} />;
    case 'EXHIBITOR_LIST':
      return <ExhibitorList {...rest} />;
    case 'EXHIBITOR_DETAILS':
      return <ExhibitorDetails {...rest} />;
    case 'EXHIBITOR_EDIT':
      return <ExhibitorEdit {...rest} />;
    case 'EXHIBITOR_REGISTRATION':
      return <ExhibitorRegistration {...rest} />;
    case 'FILE_CATALOG':
      return <FileCatalog {...rest} />;
    case 'SESSIONS':
      return <Sessions {...rest} />;
    case 'ABSTRACT_MANAGEMENT':
      return <AbstractManagement {...rest} />;
    case 'ABSTRACT_TITLE':
      return <AbstractTitle {...rest} />;
    case 'ABSTRACT_AUTHORS':
      return <AbstractAuthors {...rest} />;
    case 'ABSTRACT_TEXT':
      return <AbstractText {...rest} />;
    case 'ABSTRACT_KEYWORDS':
      return <AbstractKeywords {...rest} />;
    case 'ABSTRACT_TOPICS':
      return <AbstractTopics {...rest} />;
    case 'PRESENTATION_TYPE':
      return <PresentationType {...rest} />;
    case 'ABSTRACT_SUBMIT':
      return <AbstractSubmit {...rest} />;
    case 'ABSTRACT_REVIEW':
      return <AbstractReview {...rest} />;
    case 'SESSION_LIST':
      return <SessionList {...rest} />;
    case 'SESSION_DETAILS':
      return <SessionDetails {...rest} />;
    case 'SPEAKER_LIST':
      return <SpeakerList {...rest} />;
    case 'SPEAKER_DETAILS':
      return <SpeakerDetails {...rest} />;
    case 'ABSTRACT_LIST':
      return <AbstractList {...rest} />;
    case 'ABSTRACT_DETAILS':
      return <AbstractDetails {...rest} />;
    case 'FAVORITES':
      return <Favorites {...rest} />;
    case 'FAQ':
      return <Faq {...rest} />;
    case 'LINK':
      return (
        <div className="jir-scroll" style={{ maxHeight: 200, overflow: 'auto' }}>
          <a>Länk</a>
        </div>
      );
    default:
      return <span>Module Type {type} not found.</span>;
  }
};
